import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { apiGetTrialStatus, type TrialStatus } from '../api';
import { useAuth } from '../context/AuthContext';

/**
 * Trial banner — rendered ONCE in App.tsx (like SaveErrorBanner), so it covers
 * every signed-in route.
 *
 * Source of truth = `GET /api/auth/trial`:
 *   - 'active'  → discreet line "Trial — N days left"
 *   - 'expired' → red banner: reading still works, editing is paused
 *   - 'none'    → nothing at all (historical account with no trial: unchanged
 *                 behaviour, never nagged)
 *
 * The endpoint requires a session, so nothing is fetched while signed out —
 * a 401 would otherwise bounce a visitor off /signup back to /login.
 */
export default function TrialBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [status, setStatus] = useState<TrialStatus | null>(null);

  useEffect(() => {
    if (!user) { setStatus(null); return; }
    let cancelled = false;
    const load = () => {
      apiGetTrialStatus()
        .then((s) => { if (!cancelled) setStatus(s); })
        .catch(() => { if (!cancelled) setStatus(null); });
    };
    load();
    // Long-lived sessions: keep the day counter (and the expiry) fresh.
    const id = window.setInterval(load, 30 * 60 * 1000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [user]);

  if (!user || !status || status.trial === 'none') return null;

  if (status.trial === 'expired') {
    return (
      <div className="trial-banner trial-banner-expired" role="alert">
        <span className="trial-banner-text">
          <strong>{t('trial.expiredTitle')}</strong> {t('trial.expiredBody')}
        </span>
        <Link className="trial-banner-cta" to="/pricing">{t('trial.subscribe')}</Link>
      </div>
    );
  }

  const days = Number.isFinite(Number(status.daysLeft)) ? Math.max(0, Number(status.daysLeft)) : 0;
  return (
    <div className="trial-banner trial-banner-active" role="status">
      <span className="trial-banner-text">{t('trial.daysLeft', { days })}</span>
    </div>
  );
}